'use client';

import React, { useRef, useEffect, useState } from 'react';

const principles = [
    {
        number: '01',
        title: 'Systems over features',
        description:
            'We build cohesive systems that evolve with users — not feature lists that bloat over time. Every product decision is guided by long-term architectural thinking.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75" />
                <path d="M14 17.5h7M17.5 14v7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        number: '02',
        title: 'Intelligence by design',
        description:
            'Intelligence is not a feature we add — it is how we architect from day one. Every Codigo product is designed to learn, adapt, and get smarter with use.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z" stroke="currentColor" strokeWidth="1.75" />
                <path d="M8 12s1.5-2 4-2 4 2 4 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M9 9h.01M15 9h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        number: '03',
        title: 'Practical over theoretical',
        description:
            'We build for how people actually work, learn, and create — not how we imagine they should. Real-world constraints shape better products.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.39 0 4.56.93 6.16 2.44" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        number: '04',
        title: 'Built to last',
        description:
            'Short-term shortcuts compound into long-term problems. Codigo products are engineered for reliability, scalability, and longevity from the first line of code.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2l3 6.5H21l-5.5 4 2 6.5L12 15l-5.5 4 2-6.5L3 8.5h6L12 2z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
            </svg>
        ),
    },
];

export default function PhilosophySection() {
    const sectionRef = useRef<HTMLElement>(null);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    principles.forEach((_, i) => {
                        setTimeout(() => setActiveIndex(i), i * 120);
                    });
                }
            },
            { threshold: 0.2 }
        );
        if (sectionRef?.current) observer?.observe(sectionRef?.current);
        return () => observer?.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="py-24 px-6 md:px-10 border-t border-border"
            style={{ background: 'linear-gradient(180deg, var(--background) 0%, #0d0e18 100%)' }}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                    <div>
                        <span className="section-label text-accent mb-3 block">Our Philosophy</span>
                        <h2 className="text-section-title text-foreground">
                            How we think about<br className="hidden md:block" /> building technology.
                        </h2>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
                        Principles that guide every product decision, every architecture choice, every team hire.
                    </p>
                </div>

                {/* Principles grid */}
                <div className="grid md:grid-cols-2 gap-5">
                    {principles?.map((principle, index) => (
                        <div
                            key={principle?.number}
                            className={`group p-8 rounded-2xl border border-border transition-all duration-500 cursor-default
                ${activeIndex !== null && index <= activeIndex ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}
              `}
                            style={{
                                background: 'rgba(17,19,24,0.6)',
                                transitionDelay: `${index * 80}ms`,
                                transform: activeIndex !== null && index <= activeIndex ? 'translateY(0)' : 'translateY(24px)',
                            }}
                            onMouseEnter={() => { }}
                        >
                            <div className="flex items-start gap-5">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-primary bg-primary/10 group-hover:bg-primary/20 transition-colors">
                                    {principle?.icon}
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="section-label text-muted-foreground">{principle?.number}</span>
                                        <div className="h-px flex-1 bg-border group-hover:bg-primary/30 transition-colors" />
                                    </div>
                                    <h3 className="text-lg font-700 text-foreground mb-3 tracking-tight group-hover:text-primary transition-colors duration-300">
                                        {principle?.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{principle?.description}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}